/**
 * 606 Meta5 AI Trading - Settings Tab
 *
 * Orchestrates 4 sub-components (each extracted in iter-83):
 *   • StrategySettingsCard     — 6 strategy toggles, timeframe, symbols
 *   • BotSettingsCard          — risk %, R:R, loss limits, profit targets
 *   • MetaApiProfilesPanel     — multi-token MetaApi (Demo/Live profiles)
 *   • MetaApiAccountsSection   — connected accounts + add/manage/delete dialogs
 *
 * This file holds the shared form state, mutation handlers, and the load
 * effect. The actual render is just ~60 lines of dispatch.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTradingContext } from '../../context/TradingContext';
import { useTrading } from '../../lib/trading-i18n';
import {
  updateSettings, mt5Disconnect, mt5Status, mt5ListAccounts, mt5CreateAccount,
  mt5DeleteRemoteAccount, stopBot, fetchBotStatus, mt5Connect
} from '../../lib/trading-api';
import { toast } from 'sonner';
import MetaApiProfilesPanel from './MetaApiProfilesPanel';
import StrategySettingsCard from './settings/StrategySettingsCard';
import BotSettingsCard from './settings/BotSettingsCard';
import MetaApiAccountsSection from './settings/MetaApiAccountsSection';

const SettingsTab = ({ language = 'ar' }) => {
  const t = useTrading(language);
  const { settings, refreshSettings } = useTradingContext();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mt5Connecting, setMt5Connecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [remoteAccounts, setRemoteAccounts] = useState([]);
  const [loadingRemoteAccounts, setLoadingRemoteAccounts] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [addAccountForm, setAddAccountForm] = useState({
    name: '', login: '', password: '', server: '', mode: 'live', platform: 'mt5', connect_now: true,
  });
  const [addingAccount, setAddingAccount] = useState(false);
  // ---- MetaApi remote account management (delete to free resource slots) ----
  const [manageOpen, setManageOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null); // remote account object pending delete
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  const totalSlotsUsed = remoteAccounts.reduce((sum, a) => sum + (Number(a.resource_slots) || 1), 0);

  const handleDeleteRemoteAccount = async () => {
    if (!accountToDelete) return;
    const acc = accountToDelete;
    setDeletingAccountId(acc.account_id);
    try {
      await mt5DeleteRemoteAccount(acc.account_id);
      toast.success(language === 'ar'
        ? `✅ تم حذف الحساب ${acc.login} من MetaApi وتحرير ${acc.resource_slots || 1} slot${(acc.resource_slots || 1) > 1 ? 's' : ''}.`
        : `✅ Account ${acc.login} deleted from MetaApi — freed ${acc.resource_slots || 1} resource slot(s).`);
      setAccountToDelete(null);
      await fetchRemoteAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail
        || (language === 'ar' ? 'فشل حذف الحساب من MetaApi' : 'Failed to delete account from MetaApi'));
    } finally {
      setDeletingAccountId(null);
    }
  };

  // Detect the slot-exceeded error from the create-account flow and auto-open the manager
  const handleAddAccountError = (err) => {
    const detail = err.response?.data?.detail || '';
    const looksLikeSlotsExceeded = /resource slots|slots مستهلكة/i.test(detail);
    if (looksLikeSlotsExceeded) {
      toast.error(detail, {
        duration: 8000,
        action: {
          label: language === 'ar' ? 'إدارة الحسابات' : 'Manage',
          onClick: () => setManageOpen(true),
        },
      });
    } else {
      toast.error(detail || (language === 'ar' ? 'فشل إضافة الحساب' : 'Failed to add account'));
    }
  };

  const handleAddAccount = async () => {
    if (!addAccountForm.login || !addAccountForm.password || !addAccountForm.server) {
      toast.error(language === 'ar' ? 'املأ كل الحقول' : 'Fill in all fields');
      return;
    }
    setAddingAccount(true);
    try {
      // Auto-stop the bot before any account change
      try {
        const status = await fetchBotStatus();
        if (status?.running) {
          await stopBot();
          toast.info(language === 'ar'
            ? 'تم إيقاف البوت تلقائياً قبل إضافة الحساب.'
            : 'Bot auto-stopped before adding the account.');
        }
      } catch (e) { /* ignore */ }

      const res = await mt5CreateAccount({
        ...addAccountForm,
        login: parseInt(addAccountForm.login, 10),
      });
      if (res.warning) {
        toast.warning(res.warning);
      } else {
        toast.success(language === 'ar'
          ? `✅ تمت إضافة الحساب ${addAccountForm.login} وتفعيله`
          : `✅ Account ${addAccountForm.login} added and activated`);
      }
      setAddAccountOpen(false);
      setAddAccountForm({ name: '', login: '', password: '', server: '', mode: 'live', platform: 'mt5', connect_now: true });
      await loadMt5Status();
      await fetchRemoteAccounts();
    } catch (err) {
      handleAddAccountError(err);
    } finally {
      setAddingAccount(false);
    }
  };

  const fetchRemoteAccounts = useCallback(async () => {
    setLoadingRemoteAccounts(true);
    try {
      const data = await mt5ListAccounts();
      setRemoteAccounts(data.accounts || []);
    } catch (err) {
      console.error('Failed to list MT5 accounts:', err);
      // Silent on auto-load failure (typically METAAPI_TOKEN missing). User can hit refresh.
    } finally {
      setLoadingRemoteAccounts(false);
    }
  }, []);

  // Direct switch to a remote MetaApi account. Auto-stops the bot first if it's running.
  const switchToRemoteAccount = async (acc) => {
    setMt5Connecting(true);
    try {
      // 1) If bot is running, stop it first (account switch invalidates trading session)
      let wasRunning = false;
      try {
        const status = await fetchBotStatus();
        wasRunning = !!status?.running;
      } catch (e) { /* ignore */ }

      if (wasRunning) {
        try {
          await stopBot();
          toast.info(language === 'ar'
            ? 'تم إيقاف البوت تلقائياً قبل تبديل الحساب — شغّله يدوياً بعد التبديل.'
            : 'Bot auto-stopped before account switch — start it manually after switching.');
        } catch (e) { /* continue regardless */ }
      }

      // 2) Connect to the chosen MetaApi account
      const res = await mt5Connect({
        login: parseInt(acc.login),
        password: acc.has_saved_password ? '' : '__use_metaapi_default__',
        server: acc.server,
        mode: acc.mode || 'demo',
      });
      toast.success(res.message || (language === 'ar' ? `تم التبديل إلى ${acc.login}` : `Switched to ${acc.login}`));
      await loadMt5Status();
      await fetchRemoteAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل التبديل' : 'Switch failed'));
    } finally {
      setMt5Connecting(false);
    }
  };

  const loadMt5Status = useCallback(async () => {
    try {
      const status = await mt5Status();
      setConnectionStatus(status);
    } catch (err) {
      console.error('Failed to load MT5 status:', err);
    }
  }, []);

  useEffect(() => {
    if (settings) {
      let activeStrats = settings.active_strategies;
      if (!activeStrats || activeStrats.length === 0) {
        const legacy = settings.strategy_mode || 'smc';
        activeStrats = legacy === 'hybrid' ? ['smc', 'classic'] : [legacy];
      }
      setForm({
        mode: settings.mode || 'mock',
        symbols: settings.symbols ?? ['XAUUSD'],
        active_strategies: activeStrats,
        timeframe: settings.timeframe || '15m',
        smc_min_confidence: settings.smc_min_confidence ?? 0.55,
        risk_per_trade_pct: settings.risk_per_trade_pct ?? 1,
        risk_reward_ratio: settings.risk_reward_ratio ?? 2,
        max_open_positions: settings.max_open_positions ?? 3,
        daily_loss_limit_pct: settings.daily_loss_limit_pct ?? 5,
        monthly_loss_limit_pct: settings.monthly_loss_limit_pct ?? 15,
        use_news_filter: settings.use_news_filter ?? true,
        use_ai_confirmation: settings.use_ai_confirmation ?? true,
        news_buffer_minutes: settings.news_buffer_minutes ?? 30,
        loop_interval_seconds: settings.loop_interval_seconds ?? 15,
        ema_fast: settings.ema_fast ?? 9,
        ema_slow: settings.ema_slow ?? 21,
        rsi_period: settings.rsi_period ?? 14,
        rsi_overbought: settings.rsi_overbought ?? 70,
        rsi_oversold: settings.rsi_oversold ?? 30,
        // Profit / lot controls — must use ?? so user-saved 0 isn't reset
        daily_profit_target_usd: settings.daily_profit_target_usd ?? 0,
        quick_tp_usd: settings.quick_tp_usd ?? 0,
        manual_lot_size: settings.manual_lot_size ?? 0,
      });
    }
    loadMt5Status();
    // Auto-load MetaApi accounts so the picker is ready
    fetchRemoteAccounts().catch(() => {});
  }, [settings, loadMt5Status, fetchRemoteAccounts]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Derive strategy_mode for backward compat
      const stratMode = form.active_strategies.length === 1
        ? form.active_strategies[0]
        : (form.active_strategies.includes('smc') && form.active_strategies.includes('classic') ? 'hybrid' : form.active_strategies[0]);
      const payload = { ...form, strategy_mode: ['smc', 'classic', 'hybrid'].includes(stratMode) ? stratMode : 'smc' };
      await updateSettings(payload);
      await refreshSettings();
      toast.success(language === 'ar' ? 'تم حفظ الإعدادات' : 'Settings saved');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error(language === 'ar' ? 'فشل حفظ الإعدادات' : 'Failed to save settings');
    }
    setSaving(false);
  };

  const toggleStrategy = (id) => {
    const current = form.active_strategies || [];
    const next = current.includes(id) ? current.filter((s) => s !== id) : [...current, id];
    if (next.length === 0) {
      toast.error(language === 'ar' ? 'يجب تفعيل استراتيجية واحدة على الأقل' : 'At least one strategy must be active');
      return;
    }
    setForm({ ...form, active_strategies: next });
  };

  const handleMt5Disconnect = async () => {
    setMt5Connecting(true);
    try {
      const res = await mt5Disconnect();
      toast.success(res.message || (language === 'ar' ? 'تم قطع الاتصال' : 'Disconnected'));
      await loadMt5Status();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل قطع الاتصال' : 'Failed to disconnect');
    }
    setMt5Connecting(false);
  };

  if (!form) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isHybrid = form.active_strategies.length > 1;

  return (
    <div className="space-y-6">
      {/* Trading Mode is no longer a UI choice — it is auto-derived from the active MetaApi profile (Demo/Live) */}

      {/* Strategy Settings — extracted in iter-83 */}
      <StrategySettingsCard
        form={form}
        setForm={setForm}
        toggleStrategy={toggleStrategy}
        isHybrid={isHybrid}
        language={language}
      />

      {/* Bot Settings — extracted in iter-83 */}
      <BotSettingsCard
        form={form}
        setForm={setForm}
        handleSave={handleSave}
        saving={saving}
        t={t}
        language={language}
      />

      {/* MetaApi Profiles (multiple tokens — Demo / Live / etc.) */}
      <MetaApiProfilesPanel
        language={language}
        onActiveChanged={() => {
          // Token changed → refresh both the saved-accounts list and the connection status
          loadMt5Status();
          fetchRemoteAccounts();
        }}
      />

      {/* MetaApi Connected Accounts + Add/Manage/Delete Dialogs — extracted in iter-83 */}
      <MetaApiAccountsSection
        connectionStatus={connectionStatus}
        remoteAccounts={remoteAccounts}
        loadingRemoteAccounts={loadingRemoteAccounts}
        fetchRemoteAccounts={fetchRemoteAccounts}
        totalSlotsUsed={totalSlotsUsed}
        t={t}
        language={language}
        switchToRemoteAccount={switchToRemoteAccount}
        handleMt5Disconnect={handleMt5Disconnect}
        mt5Connecting={mt5Connecting}
        addAccountOpen={addAccountOpen}
        setAddAccountOpen={setAddAccountOpen}
        addAccountForm={addAccountForm}
        setAddAccountForm={setAddAccountForm}
        handleAddAccount={handleAddAccount}
        addingAccount={addingAccount}
        manageOpen={manageOpen}
        setManageOpen={setManageOpen}
        accountToDelete={accountToDelete}
        setAccountToDelete={setAccountToDelete}
        handleDeleteRemoteAccount={handleDeleteRemoteAccount}
        deletingAccountId={deletingAccountId}
      />
    </div>
  );
};

export default SettingsTab;
